<template>
  <div class="exams-filter">
    <div
      v-if="enabledStatus?.length"
      class="exams-filter-status-container"
    >
      <autocomplete
        v-model="model.status"
        :options="statusOptions"
        placeholder="Status"
      />
    </div>

    <div class="exams-filter-variable-control-container">
      <div class="controls-group">
        <div class="exams-filter-variable-select">
          <autocomplete
            v-model="model.variableControlType"
            :options="variableControlOptions"
          />
        </div>

        <div class="exams-filter-variable-control">
          <date-picker-range
            v-if="model.variableControlType === VARIABLE_CONTROL_TYPE_DATE_OPTION"
            :date-selected.sync="date"
            :limit-data="limitDateForTraceability"
            :default-start-date="today"
            :default-end-date="today"
            :clear-button="false"
            calendar-direction="top"
            date-filter-alternative
          />

          <input
            v-else
            class="text-control"
            v-model="model.query"
            :placeholder="variableControlPlaceholder"
          />
        </div>
      </div>
    </div>

    <md-button
      type="button"
      class="btn btn-primary"
      @click="handleSubmit"
    >
      Consultar
    </md-button>
  </div>
</template>

<script>
import moment from 'moment'

import {
  STATUS_EMPTY_OPTION,
  VARIABLE_CONTROL_TYPE_OS_ALVARO_OPTION,
  VARIABLE_CONTROL_TYPE_DATE_OPTION,
  VARIABLE_CONTROL_TYPE_OS_LIS_OPTION,
  VARIABLE_CONTROL_TYPE_BATCH_OPTION,
  VARIABLE_CONTROL_TYPE_PATIENT_NAME_OPTION,
  VARIABLE_CONTROL_TYPE_SAMPLE_OPTION,
} from '@/enums'

import { traceabilityStatusLabels } from '@/constants'

import strings from '@/strings'

import Autocomplete from '@/components/common/Autocomplete'
import DatePickerRange from '@/components/common/DatePickerRange'

export default {
  name: 'ExamsFilter',
  components: {
    Autocomplete,
    DatePickerRange,
  },
  props: {
    enabledStatus: {
      type: Array,
    },
  },
  data() {
    return {
      model: {
        status: STATUS_EMPTY_OPTION,
        query: undefined,
        variableControlType: VARIABLE_CONTROL_TYPE_OS_ALVARO_OPTION,
      },
      VARIABLE_CONTROL_TYPE_DATE_OPTION,
      date: {
        start: new Date(),
        end: new Date(),
      },
      limitDaysTraceability: strings.limitDaysForCalendar.traceability,
    }
  },
  computed: {
    statusOptions() {
      return [
        STATUS_EMPTY_OPTION,
        ...this.enabledStatus.map(status => ({
          label: traceabilityStatusLabels[status],
          value: status,
        })),
      ]
    },
    variableControlOptions() {
      return [
        VARIABLE_CONTROL_TYPE_OS_ALVARO_OPTION,
        VARIABLE_CONTROL_TYPE_OS_LIS_OPTION,
        VARIABLE_CONTROL_TYPE_DATE_OPTION,
        VARIABLE_CONTROL_TYPE_PATIENT_NAME_OPTION,
        VARIABLE_CONTROL_TYPE_SAMPLE_OPTION,
        VARIABLE_CONTROL_TYPE_BATCH_OPTION,
      ]
    },
    limitDateForTraceability() {
      return moment(new Date()).subtract(this.limitDaysTraceability, 'days').toDate()
    },
    today() {
      return moment(new Date()).format('DD/MM/YYYY')
    },
    variableControlPlaceholder() {
      switch (this.model.variableControlType?.value) {
        case 'os_aol':
          return 'Número da OS Alvaro'

        case 'os_lis':
          return 'Número da OS Lis'

        case 'patientName':
          return 'Nome do paciente'

        case 'sample':
          return 'Código de barras da amostra'

        case 'batch':
          return 'Código de barras do lote'
      }

      return undefined
    },
  },
  mounted() {
    this.defineFilter()
  },
  methods: {
    handleSubmit() {
      const filter = {}

      if (this.model.status !== STATUS_EMPTY_OPTION) {
        filter.status = this.model.status?.value
      }

      if (this.model.variableControlType === VARIABLE_CONTROL_TYPE_DATE_OPTION) {
        filter.date = this.date
      } else if (this.model.query) {
        filter.query = this.model.query
      }

      this.$emit('submit', filter)
    },
    defineFilter() {
      const typeOfSearch = this.$route.query.typeOfSearch
      const query = this.$route.query.q

      switch (typeOfSearch) {
        case 'BA':
          this.model.variableControlType = VARIABLE_CONTROL_TYPE_BATCH_OPTION
          this.model.query = query
          break
        case 'OA':
          this.model.variableControlType = VARIABLE_CONTROL_TYPE_OS_ALVARO_OPTION
          this.model.query = query
          break
        case 'PN':
          this.model.variableControlType = VARIABLE_CONTROL_TYPE_PATIENT_NAME_OPTION
          this.model.query = query
          break
        case 'AM':
          this.model.variableControlType = VARIABLE_CONTROL_TYPE_SAMPLE_OPTION
          this.model.query = query
          break
        case 'OL':
          this.model.variableControlType = VARIABLE_CONTROL_TYPE_OS_LIS_OPTION
          this.model.query = query
          break
        case 'DT':
          this.model.variableControlType = VARIABLE_CONTROL_TYPE_DATE_OPTION
          this.date.start = this.$route.query.startDate
            ? new Date(this.$route.query.startDate)
            : new Date()
          this.date.end = this.$route.query.endDate
            ? new Date(this.$route.query.endDate)
            : new Date()
          break

        default:
          this.model.variableControlType = VARIABLE_CONTROL_TYPE_OS_ALVARO_OPTION
          this.model.query = undefined
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.exams-filter {
  display: flex;
  gap: 0.5rem;

  &-status {
    &-container {
      width: 250px;
    }
  }

  &-variable {
    &-select {
      width: 200px;
    }

    &-control {
      flex: 1;

      &-container {
        flex: 1;
      }
    }
  }

  .md-button {
    height: 40px;
  }

  ::v-deep(.multiselect__tags) {
    height: 40px;
    padding-top: 7px;
  }
}

.text-control {
  width: 100%;
  padding: 0.75rem 0.5rem;
  border-radius: 0.25rem;
  border: 1px solid #e8e8e8;
  background-color: #ffffff;
  font-family: 'Exo 2';
  font-weight: 500;
  font-size: 0.875rem;
  color: #999999;
  outline: none;
  height: 40px;
}

::v-deep(.search-bar__date) {
  width: 100%;
  padding: 0 0.5rem;
  border-radius: 0.25rem;
  border: 1px solid #e8e8e8;
  background-color: #ffffff;
  font-weight: 500;
  font-size: 0.875rem;
  color: #999999;
  height: 40px;
  margin: 0;
  display: flex;
  align-items: center;
}

::v-deep(.md-input-container) {
  min-height: auto;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

::v-deep(.alternative),
::v-deep(.popover-container) {
  width: 100%;
}

.controls-group {
  display: flex;

  > :first-child {
    ::v-deep(.multiselect__tags),
    ::v-deep(.search-bar__date),
    .text-control {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }

  > :last-child {
    ::v-deep(.multiselect__tags),
    ::v-deep(.search-bar__date),
    .text-control {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }

  > * + * {
    ::v-deep(.multiselect__tags),
    ::v-deep(.search-bar__date),
    .text-control {
      border-left: none;
    }
  }
}
</style>
